import { useEffect, useState, useCallback } from "react";
import { listEntries } from "../api";
import { STATE_STYLES, PHASE_COLORS, CARD } from "../lib/planner";
import {
  LineChart, Line, ScatterChart, Scatter, Cell,
  ResponsiveContainer, XAxis, YAxis, Tooltip, ZAxis,
} from "recharts";
import { CalendarDays } from "lucide-react";

const CHART_MARGIN = { top: 5, right: 5, bottom: 15, left: 5 };
const TICK_STYLE = { fontSize: 11, fill: "#a8a29e" };
const TOOLTIP_STYLE = { borderRadius: 12, border: "1px solid #f5f5f4", fontSize: 12 };
const Y_DOMAIN = [0, 100];
const LINE_DOT = { r: 3 };

const CycleDot = ({ v }) => (
  <span className="flex items-center gap-1.5 text-xs text-stone-500 dark:text-stone-400">
    <span className="h-2.5 w-2.5 rounded-full" style={{ background: PHASE_COLORS[v] }} />
    {v}
  </span>
);

export const History = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    listEntries()
      .then(setEntries)
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const chartData = [...entries].reverse().map((e) => ({ date: e.date.slice(5), capacity: e.capacity_pct }));
  const scatterData = entries.map((e) => ({
    cycle_day: e.cycle_day,
    capacity: e.capacity_pct,
    phase: e.phase,
    date: e.date.slice(5),
  }));

  return (
    <div className="space-y-6" data-testid="history-page">
      <h2 className="font-heading text-3xl font-bold text-[#3b2a24] dark:text-stone-100">Your history</h2>

      {loading ? (
        <p className="text-stone-400">Loading…</p>
      ) : entries.length === 0 ? (
        <div className={`${CARD} p-10 text-center`}>
          <CalendarDays className="h-10 w-10 text-stone-300 mx-auto mb-3" />
          <p className="text-stone-500 dark:text-stone-400">No entries yet. Your history builds up from day one.</p>
        </div>
      ) : (
        <>
          {/* Capacity vs cycle day, colored by phase */}
          <div className={`${CARD} p-6`} data-testid="cycle-trend-chart">
            <p className="text-xs font-bold uppercase tracking-[0.15em] text-stone-400 mb-4">
              Capacity by cycle day
            </p>
            <ResponsiveContainer width="100%" height={200}>
              <ScatterChart margin={CHART_MARGIN}>
                <XAxis
                  type="number" dataKey="cycle_day" name="Cycle day"
                  domain={[1, "dataMax"]} tick={TICK_STYLE} axisLine={false} tickLine={false}
                  label={{ value: "cycle day", position: "insideBottom", offset: -8, fontSize: 10, fill: "#a8a29e" }}
                />
                <YAxis type="number" dataKey="capacity" domain={Y_DOMAIN} tick={TICK_STYLE} axisLine={false} tickLine={false} width={28} />
                <ZAxis range={[80, 80]} />
                <Tooltip contentStyle={TOOLTIP_STYLE} cursor={{ strokeDasharray: "3 3" }} />
                <Scatter data={scatterData}>
                  {scatterData.map((d) => (
                    <Cell key={`${d.date}-${d.cycle_day}`} fill={PHASE_COLORS[d.phase] || "#78716c"} />
                  ))}
                </Scatter>
              </ScatterChart>
            </ResponsiveContainer>
            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3">
              {Object.keys(PHASE_COLORS).map((p) => <CycleDot key={p} v={p} />)}
            </div>
          </div>

          {/* Capacity over time */}
          {chartData.length > 1 && (
            <div className={`${CARD} p-6`}>
              <p className="text-xs font-bold uppercase tracking-[0.15em] text-stone-400 mb-4">Capacity over time</p>
              <ResponsiveContainer width="100%" height={160}>
                <LineChart data={chartData} margin={CHART_MARGIN}>
                  <XAxis dataKey="date" tick={TICK_STYLE} axisLine={false} tickLine={false} />
                  <YAxis domain={Y_DOMAIN} tick={TICK_STYLE} axisLine={false} tickLine={false} width={28} />
                  <Tooltip contentStyle={TOOLTIP_STYLE} />
                  <Line type="monotone" dataKey="capacity" stroke="#a8a29e" strokeWidth={2.5} dot={LINE_DOT} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          <div className="space-y-3">
            {entries.map((e) => {
              const s = STATE_STYLES[e.label] || STATE_STYLES.Steady;
              return (
                <div
                  key={e.date}
                  data-testid={`history-row-${e.date}`}
                  className={`rounded-2xl border ${s.border} ${s.bg} p-4 flex items-center justify-between`}
                >
                  <div>
                    <p className={`font-semibold ${s.text}`}>{e.date}</p>
                    <p className={`text-xs ${s.subtext}`}>
                      {e.phase} · day {e.cycle_day} · sleep {e.sleep_score}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className={`font-heading text-2xl font-bold ${s.text}`}>{e.capacity_pct}%</p>
                    <p className={`text-xs ${s.subtext}`}>{e.label}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};
